import { useState } from "react";
import { Check, ArrowRight, Mail, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal } from "./Reveal";
import { PRICE, FEATURES, ADDONS } from "../data/pricing";

const ICONS = { mail: Mail, help: HelpCircle };

export const PricingSection = () => {
  const [currency, setCurrency] = useState("GBP");
  const p = PRICE[currency];
  const navigate = useNavigate();

  return (
    <div className="grid lg:grid-cols-2 gap-8 items-start">
      {/* Pricing panel */}
      <Reveal>
        <div className="glass-panel h-full px-6 py-10 md:px-12 md:py-12">
          <p className="text-xs font-bold tracking-[0.22em] text-[#39D5FF] mb-4">TRANSPARENT PRICING</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#F7FAFF]">
            Simple, <span className="text-gradient">Honest Pricing.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#AEBFD8] leading-relaxed">
            One fixed price. No monthly fees. No hidden costs. Everything you need to get your business online.
          </p>

          <div className="mt-7 inline-flex p-1 rounded-full bg-[#06152D]/80 border border-[rgba(67,157,255,0.35)]" data-testid="pricing-toggle">
            {["GBP", "USD"].map((c) => (
              <button key={c} onClick={() => setCurrency(c)} data-testid={`currency-${c}`}
                className={`px-6 py-2 rounded-full text-sm font-bold transition-all min-h-[40px] ${
                  currency === c ? "btn-primary" : "text-[#AEBFD8] hover:text-[#F7FAFF]"
                }`}>
                {PRICE[c].label}
              </button>
            ))}
          </div>

          <div className="glass-card mt-8 p-6 md:p-8 relative overflow-hidden" data-testid="pricing-card">
            <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-[radial-gradient(circle,rgba(0,168,255,0.25),transparent_70%)] blur-2xl pointer-events-none" />
            <h3 className="text-xl font-bold text-[#F7FAFF] relative z-10">Core Website Package</h3>
            <div className="mt-5 flex items-end gap-2 relative z-10">
              <span className="text-5xl md:text-6xl font-extrabold text-[#F7FAFF]">{p.symbol}{p.base}</span>
              <span className="text-sm text-[#7F92AE] mb-2">{p.region}</span>
            </div>
            <ul className="mt-6 space-y-2.5 relative z-10">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-[#AEBFD8]">
                  <Check size={16} className="text-[#45E0B5] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button onClick={() => navigate("/contact")} data-testid="pricing-get-started"
              className="btn-primary mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-bold min-h-[44px] relative z-10">
              Get Started <ArrowRight size={18} />
            </button>
          </div>

          <p className="mt-5 text-xs text-[#7F92AE] leading-relaxed">
            After the first year, your domain will need to be renewed directly with the registrar — typically
            £10–15/year. Your tutorial document walks you through exactly how to do it.
          </p>
        </div>
      </Reveal>

      {/* Add-ons panel */}
      <Reveal delay={0.1}>
        <div id="add-ons" className="glass-panel h-full px-6 py-10 md:px-12 md:py-12 relative overflow-hidden">
          <img src="/assets/cube.png" alt="" aria-hidden="true"
            className="absolute right-2 bottom-2 w-40 opacity-60 pointer-events-none drop-shadow-[0_0_30px_rgba(0,168,255,0.4)]" />
          <p className="text-xs font-bold tracking-[0.22em] text-[#39D5FF] mb-4">OPTIONAL ADD-ONS</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#F7FAFF]">
            Enhance <span className="text-gradient">Your Website.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#AEBFD8] leading-relaxed">
            Add extra features to your website for a one-off fee.
          </p>

          <div className="mt-8 flex flex-col gap-5 relative z-10">
            {ADDONS.map((a, i) => {
              const Icon = ICONS[a.icon];
              return (
                <Reveal key={a.title} delay={i * 0.08}>
                  <div className="glass-card p-5 flex items-start gap-4" data-testid="addon-row">
                    <span className="grid place-items-center w-12 h-12 shrink-0 rounded-xl bg-[rgba(0,168,255,0.15)] border border-[rgba(65,176,255,0.4)]">
                      <Icon size={22} className="text-[#39D5FF]" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-[#F7FAFF]">{a.title}</h3>
                      <p className="text-sm text-[#AEBFD8] leading-snug mt-0.5">{a.desc}</p>
                      {a.benefit && <p className="text-xs text-[#45E0B5] mt-1.5 font-medium">{a.benefit}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-extrabold text-[#F7FAFF]">{currency === "GBP" ? a.gbp : a.usd}</div>
                      <div className="text-[11px] text-[#7F92AE]">one-off</div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <p className="mt-6 text-xs text-[#7F92AE] relative z-10">All add-ons are one-off fees — no recurring charges.</p>
        </div>
      </Reveal>
    </div>
  );
};
